import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * hallmark critique · AlertCard (ticket-printer edition)
 * tokens locked from docs/design.md §5 (Card variants) + §10 (Bunching Alert Card)
 * states covered: active (clay) · acknowledged (paper) · resolved (paper low) · dismissed (paper low)
 *
 * Phase 17-poka · reimagined as a receipt-printer ticket:
 *   - perforated dashed edges top + bottom
 *   - JetBrains Mono type
 *   - staggered "print" reveal: header prints first, then each field row,
 *     then the action buttons; the card itself grows from a zero height
 *     like paper feeding out of a printer
 *   - one clay-ember ticket per viewport is preserved (BunchingRail owns
 *     that invariant); everything else is off-white receipt paper
 */
import { useState } from "react";
import { PillButton } from "../common/PillButton";
import { AlertExplainDialog } from "../copilot/AlertExplainDialog";
function formatSeconds(s) {
    const min = Math.floor(s / 60);
    const sec = Math.round(s - min * 60);
    return min > 0 ? `${min}m ${sec.toString().padStart(2, "0")}s` : `${sec}s`;
}
function displayStateLabel(state) {
    switch (state) {
        case "active":
            return "ACTIVE ALERT";
        case "acknowledged":
            return "ACKNOWLEDGED";
        case "resolved":
            return "RESOLVED";
        case "dismissed":
            return "DISMISSED";
        default:
            return "ALERT";
    }
}
// ---- ticket edges: real paper cut-out zig-zag via CSS mask ------------
// The `.bb-paper-cutout` class (globals.css) tiles two SVG triangles at
// top+bottom to cut a proper sawtooth into the article — 9px teeth. Content
// is safely inset by 20px+ padding so it clears the valleys.
const layoutStyle = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    // Extra top+bottom padding so the zig-zag teeth don't overlap the
    // ACTIVE ALERT / LEAD labels. Was 18/16 → now 24/22.
    padding: "24px 20px 22px 20px",
    borderRadius: 0,
    fontFamily: "var(--font-mono, 'JetBrains Mono', monospace)",
    boxShadow: "none",
    border: "none",
    // Receipt-printer feed: the article's clip-path opens top-down over
    // 1.6 s (paper extrusion pace), letting each row underneath emerge with
    // its own left→right typewriter reveal via bb-row-print. Row delays
    // are tuned so each row prints in place as the clip-path passes its
    // vertical position.
    animation: "bb-ticket-print 1.6s cubic-bezier(0.22, 0.8, 0.28, 1) both",
    transformOrigin: "top center",
    willChange: "clip-path, transform",
};
const clayStyle = {
    ...layoutStyle,
    backgroundColor: "var(--color-clay-ember)",
    color: "var(--color-pure-white)",
    // paper texture — faint diagonal weave, low contrast on clay
    backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0 1px, transparent 1px 3px)",
};
const whiteStyle = {
    ...layoutStyle,
    // slight cream so it reads as receipt paper, not screen white
    backgroundColor: "#faf7f0",
    color: "var(--color-deep-ink)",
    // No border — the CSS mask cuts the outer edge into zig-zags; a
    // rectangular border would be sliced into segments.
    border: "none",
    backgroundImage: "repeating-linear-gradient(45deg, rgba(0,0,0,0.018) 0 1px, transparent 1px 3px)",
};
// each ticket row prints in with a staggered delay — configured via the
// `--bb-row-delay` custom property that we assign per row below.
const rowBaseStyle = {
    animation: "bb-row-print 0.42s cubic-bezier(0.2, 0.9, 0.1, 1) both",
};
function rowStyle(delaySec) {
    return { ...rowBaseStyle, animationDelay: `${delaySec}s` };
}
// Auditorium-tuned pill labels — 13px (was 11.5), 800 weight, 0.16em
// tracking (Inter is wider than Big Shoulders so it needs less tracking
// to breathe). These row headers are the audience's scan anchors.
const labelStyle = (isClay) => ({
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    opacity: isClay ? 0.9 : 1,
    color: isClay ? "var(--color-pure-white)" : "var(--color-cool-ash)",
});
const valueStyle = {
    fontSize: 16,
    fontWeight: 700,
    fontFamily: "var(--font-mono, 'JetBrains Mono', monospace)",
    lineHeight: 1.4,
    letterSpacing: "0.01em",
    margin: 0,
    wordBreak: "break-word",
};
export function AlertCard({ alert, variant, onHold, onDismiss, }) {
    const isClay = variant === "clay";
    const [leader, follower] = alert.vehicles;
    const [explainOpen, setExplainOpen] = useState(false);
    const ruleColor = isClay ? "rgba(255,255,255,0.35)" : "var(--color-pebble)";
    return (_jsxs("article", { className: "bb-paper-cutout", style: isClay ? clayStyle : whiteStyle, "data-testid": `alert-card-${alert.id}`, "data-variant": variant, "data-display-state": alert.displayState, "aria-live": alert.displayState === "active" ? "assertive" : "off", children: [_jsxs("div", { style: { ...rowStyle(0.10), display: "flex", justifyContent: "space-between", gap: 12 }, children: [_jsx("span", { style: labelStyle(isClay), children: displayStateLabel(alert.displayState) }), _jsxs("span", { style: labelStyle(isClay), children: ["LEAD ", formatSeconds(alert.forecast_lead_s)] })] }), _jsx("div", { "aria-hidden": "true", style: {
                    ...rowStyle(0.25),
                    height: 1,
                    background: ruleColor,
                    margin: "2px 0",
                } }), _jsx("h4", { style: {
                    ...rowStyle(0.38),
                    fontFamily: "var(--font-display), sans-serif",
                    fontSize: 24,
                    lineHeight: 1.2,
                    fontWeight: 900,
                    letterSpacing: "-0.015em",
                    color: "inherit",
                    margin: 0,
                }, children: alert.headline }), _jsxs("dl", { style: {
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px 14px",
                    margin: 0,
                }, children: [_jsxs("div", { style: rowStyle(0.55), children: [_jsx("dt", { style: labelStyle(isClay), children: "PAIR" }), _jsxs("dd", { style: valueStyle, children: [leader, " \u2192 ", follower] })] }), _jsxs("div", { style: rowStyle(0.68), children: [_jsx("dt", { style: labelStyle(isClay), children: "FORECAST GAP" }), _jsxs("dd", { style: valueStyle, children: [Math.round(alert.forecast_gap_s), "s"] })] }), _jsxs("div", { style: rowStyle(0.80), children: [_jsx("dt", { style: labelStyle(isClay), children: "HOLD AT" }), _jsx("dd", { style: valueStyle, children: alert.suggested_hold_stop })] }), _jsxs("div", { style: rowStyle(0.94), children: [_jsx("dt", { style: labelStyle(isClay), children: "SUGGESTED" }), _jsxs("dd", { style: valueStyle, children: [alert.suggested_hold_s, "s"] })] })] }), _jsx("div", { "aria-hidden": "true", style: {
                    ...rowStyle(1.06),
                    height: 1,
                    background: ruleColor,
                    margin: "4px 0 2px",
                    borderTop: `1px dashed ${ruleColor}`,
                    backgroundColor: "transparent",
                } }), _jsxs("div", { style: { ...rowStyle(1.20), display: "flex", gap: 8, flexWrap: "wrap" }, children: [_jsxs(PillButton, { size: "sm", variant: isClay ? "filled" : "outline", onClick: () => onHold?.(alert.id), disabled: alert.displayState !== "active", "data-testid": `alert-hold-${alert.id}`, children: ["Hold ", alert.suggested_hold_s, "s"] }), _jsx(PillButton, { size: "sm", variant: isClay ? "ghost" : "outline", onClick: () => onDismiss?.(alert.id), "data-testid": `alert-dismiss-${alert.id}`, style: isClay
                            ? { borderColor: "rgba(255,255,255,0.6)" }
                            : { color: "var(--color-cool-ash)", borderColor: "var(--color-pebble)" }, children: "Dismiss" }), _jsx(PillButton, { size: "sm", variant: isClay ? "ghost" : "outline", onClick: () => setExplainOpen(true), "data-testid": `alert-explain-${alert.id}`, style: isClay
                            ? { borderColor: "rgba(255,255,255,0.6)" }
                            : { color: "var(--color-cool-ash)", borderColor: "var(--color-pebble)" }, children: "Explain" })] }), explainOpen ? (_jsx(AlertExplainDialog, { alert: alert, onClose: () => setExplainOpen(false) })) : null] }));
}
